
import { Button } from "@/components/ui/button"
import { UserRound, Settings, LogOut, Key, LayoutDashboard, FolderCog, Building2, CreditCard, Calendar } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useQuery } from '@tanstack/react-query'
import { LanguageSelector } from "../LanguageSelector"
import { WorkspaceSelector } from "../workspace/WorkspaceSelector"
import { WorkspaceMembers } from "../workspace/WorkspaceMembers"
import { LanguagePreferences } from "../settings/LanguagePreferences"
import { CreateBoardDialog } from "../board/CreateBoardDialog"
import { PlanBadge } from "../subscription/PlanBadge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { supabase } from "@/integrations/supabase/client"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { useEffect, useState } from "react"
import { ThemeToggle } from "../ui/ThemeToggle"
import { useQueryClient } from "@tanstack/react-query"
import { SubscriptionProvider } from "@/contexts/SubscriptionContext"

interface UserProfile {
  full_name: string | null;
  avatar_url: string | null;
  email: string;
}

interface TopNavProps {
  onWorkspaceSelect?: (workspaceId: string) => void;
}

export function TopNav({ onWorkspaceSelect }: TopNavProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: profileData, error } = await supabase
            .from('profiles')
            .select('full_name, avatar_url, email')
            .eq('id', session.user.id)
            .single();

          if (error) {
            console.error('Error fetching profile:', error);
            return;
          }

          setProfile(profileData);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };

    fetchProfile();
  }, []);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/auth');
      toast.success(t('Signed out successfully'));
    } catch (error) {
      toast.error(t('Error signing out'));
    }
  };

  const handleChangePassword = async () => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(profile?.email || '', {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) throw error;
      toast.success(t('Password reset email sent'));
    } catch (error) {
      toast.error(t('Error sending password reset email'));
    }
  };

  const handleBoardCreated = () => {
    queryClient.invalidateQueries({ queryKey: ['boards'] });
  };

  const handleManageWorkspaces = () => {
    navigate('/manage-workspaces');
  };

  const handleSubscriptions = () => {
    navigate('/subscriptions');
  };

  return (
    <div className="h-14 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 items-center gap-4 px-6">
        <a href="/" className="flex items-center gap-2 font-semibold">
          <LayoutDashboard className="h-6 w-6 text-primary" />
          <span>Kanflows</span>
        </a>
        
        <div className="flex items-center gap-2">
          <WorkspaceSelector onWorkspaceSelect={onWorkspaceSelect} />
          <CreateBoardDialog onBoardCreated={handleBoardCreated} />
        </div>
        <WorkspaceMembers workspaceId="default" />

        <div className="ml-auto flex items-center gap-4">
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <ThemeToggle />
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="relative h-9 w-9 rounded-full bg-background"
              >
                <Avatar className="h-9 w-9">
                  <AvatarImage src={profile?.avatar_url || ''} alt="Profile" />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    <UserRound className="h-5 w-5" />
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              className="w-56 bg-white" 
              align="end" 
              forceMount
              sideOffset={5}
              collisionPadding={10}
            >
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {profile?.full_name || t('User')}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {profile?.email}
                  </p>
                  <div className="pt-1">
                    <SubscriptionProvider>
                      <PlanBadge />
                    </SubscriptionProvider>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>{t('Settings')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate('/integrations')}>
                  <Calendar className="mr-2 h-4 w-4" />
                  <span>{t('Integrations')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleManageWorkspaces}>
                  <FolderCog className="mr-2 h-4 w-4" />
                  <span>{t('Manage workspaces')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate('/manage-organizations')}>
                  <Building2 className="mr-2 h-4 w-4" />
                  <span>{t('Manage organizations')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleSubscriptions}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  <span>{t('Billing & Subscriptions')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleChangePassword}>
                  <Key className="mr-2 h-4 w-4" />
                  <span>{t('Change password')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <LanguagePreferences />
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>{t('Sign out')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
